import { avaliarResposta, type Marcado, type Veredito } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import {
  contarPendentes,
  enfileirar,
  lerDoPacote,
  lerFila,
  tirarDaFila,
  type RespostaPendente,
} from "./fila.js";

/**
 * Envio de respostas com queda para a fila, e o esvaziamento dela.
 *
 * **O veredito offline sai da mesma função pura que o servidor usa** —
 * `avaliarResposta`, de `@noesis/shared`. Uma implementação só: offline e online
 * não podem discordar sobre se você acertou, porque não há duas contas.
 *
 * **Nada é perdido e nada é duplicado.** O `idCliente` já nasce no navegador
 * (invariante 12) e o servidor tem índice único em `(usuarioId, idCliente)`:
 * mandar de novo devolve `repetida: true` e não conta duas vezes. É por isso que
 * a fila pode reenviar sem medo — a garantia já existia, com teste, desde a F1.
 */

export interface EnvioLocal {
  veredito: Veredito;
  /** `true` quando a resposta ficou na fila e ainda não chegou ao servidor. */
  pendente: boolean;
}

/**
 * Monta o veredito com o gabarito baixado, quando não há rede.
 *
 * Devolve `null` se o item não foi baixado — o que acontece quando o modo
 * ônibus está desligado. Nesse caso não há feedback a dar, e a tela precisa
 * dizer isso em vez de inventar um.
 */
export async function vereditoLocal(
  itemId: string,
  marcado: Marcado,
  limiarAutoavaliacao: number,
): Promise<Veredito | null> {
  const guardado = await lerDoPacote(itemId);
  if (!guardado) return null;

  const acertou = avaliarResposta(
    { tipo: guardado.tipo, ...((guardado.gabarito ?? {}) as object), ...((guardado.conteudo ?? {}) as object) },
    marcado,
    limiarAutoavaliacao,
  );

  const g = guardado.gabarito as Record<string, unknown>;
  const c = guardado.conteudo as { lacunas?: { id: string; resposta: string }[] };
  const gabarito: Veredito["gabarito"] =
    guardado.tipo === "multipla"
      ? { tipo: "multipla", correta: g.correta as "A" | "B" | "C" | "D" | "E" }
      : guardado.tipo === "certo_errado"
        ? { tipo: "certo_errado", correta: Boolean(g.correta) }
        : guardado.tipo === "cloze"
          ? { tipo: "cloze", lacunas: (c.lacunas ?? []).map((l) => ({ id: l.id, resposta: l.resposta })) }
          : null;

  return {
    itemId,
    acertou,
    gabarito,
    explicacao: guardado.explicacao,
    repetida: false,
    // O agendamento é do servidor: o SM-2 depende do estado anterior, que não
    // está no aparelho. Ele aparece quando a resposta sincronizar.
    agendamento: null,
  };
}

function semRede(e: unknown): boolean {
  return e instanceof FalhaDaApi && e.codigo === "rede";
}

/**
 * Envia a resposta; se não houver rede, guarda na fila e responde com o
 * veredito local.
 */
export async function responderComQueda(
  entrada: { idCliente: string; itemId: string; marcado: Marcado; tempoMs: number },
  limiarAutoavaliacao: number,
): Promise<EnvioLocal & { atual?: Awaited<ReturnType<typeof pedir<"estudo.responder">>>["atual"] }> {
  try {
    const r = await pedir("estudo.responder", { corpo: entrada });
    return { veredito: r.veredito, pendente: false, atual: r.atual };
  } catch (e) {
    if (!semRede(e)) throw e;

    const local = await vereditoLocal(entrada.itemId, entrada.marcado, limiarAutoavaliacao);
    if (!local) {
      // Sem gabarito no aparelho não há veredito honesto a dar. A tela precisa
      // dizer isso — inventar um "acertou" seria pior do que a falha.
      throw new FalhaDaApi(
        "rede",
        "Sem conexão, e este item não foi baixado para uso offline. Ligue o modo ônibus antes de sair de casa.",
      );
    }

    await enfileirar({ ...entrada, criadaEm: new Date().toISOString(), tentativas: 0 });
    return { veredito: local, pendente: true };
  }
}

export interface ResultadoDaSincronia {
  enviadas: number;
  jaEstavam: number;
  restantes: number;
  /**
   * Quantas o servidor recusou — datação fora do teto, sessão vencida, item
   * apagado. Elas saem da fila, e por isso precisam aparecer: até a RN-64
   * ganhar teto de datação (ADR 027), a resposta recusada sumia em silêncio.
   */
  recusadas: number;
  /** A mensagem da primeira recusa, que é a que a tela mostra. */
  motivoRecusa: string | null;
}

/**
 * Esvazia a fila, na ordem em que as respostas foram dadas.
 *
 * A ordem importa: duas respostas ao mesmo item precisam chegar na sequência
 * certa, senão o SM-2 aplicaria o segundo veredito antes do primeiro.
 *
 * Para na primeira falha de rede — não adianta insistir sem sinal, e o que
 * sobrou continua guardado para a próxima tentativa.
 */
export async function esvaziarFila(): Promise<ResultadoDaSincronia> {
  const pendentes = (await lerFila()).sort((a, b) => a.criadaEm.localeCompare(b.criadaEm));
  let enviadas = 0;
  let jaEstavam = 0;
  let recusadas = 0;
  let motivoRecusa: string | null = null;

  for (const p of pendentes) {
    try {
      const r = await pedir("estudo.responder", {
        corpo: {
          idCliente: p.idCliente,
          itemId: p.itemId,
          marcado: p.marcado,
          tempoMs: p.tempoMs,
          // O instante real da resposta, não o da sincronia. É ele que faz o
          // servidor aceitá-la numa sessão que expirou depois (RN-64) e que a
          // põe no dia certo quando a fila atravessa a meia-noite.
          respondidoEm: p.criadaEm,
        },
      });
      if (r.veredito.repetida) jaEstavam += 1;
      else enviadas += 1;
      await tirarDaFila(p.idCliente);
    } catch (e) {
      if (semRede(e)) break;
      // Erro que não é de rede — item apagado, sessão vencida, datação fora do
      // teto — não melhora com insistência. Sai da fila para não travar as
      // respostas seguintes, que é o que aconteceria se ela parasse aqui para
      // sempre. Mas sair em silêncio era perder a resposta sem dizer: a partir
      // do ADR 027 a faixa diz quantas caíram e por quê.
      recusadas += 1;
      motivoRecusa ??= e instanceof FalhaDaApi ? e.message : "O servidor não aceitou esta resposta.";
      await tirarDaFila(p.idCliente);
    }
  }

  return { enviadas, jaEstavam, recusadas, motivoRecusa, restantes: await contarPendentes() };
}

/** Baixa os gabaritos da sessão e das revisões de hoje. Modo ônibus ligado. */
export async function baixarPacote(): Promise<number> {
  const r = await pedir("estudo.pacote");
  const { guardarPacote } = await import("./fila.js");
  await guardarPacote(
    r.itens.map((i) => ({
      itemId: i.itemId,
      tipo: i.tipo,
      conteudo: i.conteudo,
      gabarito: i.gabarito,
      explicacao: i.explicacao,
    })),
  );
  return r.itens.length;
}

export type { RespostaPendente };
